import type {
  FleetActivityCeilings,
  FleetConfigActivity,
  FleetSettingCeiling,
  FleetSettings,
  FleetSettingValue
} from './claude-fleet.types'

/** How a setting is edited: the input each kind renders as on the Settings tab. */
export type FleetSettingKind =
  | 'switch'
  | 'time'
  | 'times'
  | 'number'
  | 'one-of'
  | 'multi'
  | 'emails'
  | 'models'
  | 'email-numbers'
  | 'text'

export interface FleetSettingField {
  key: string
  kind: FleetSettingKind
  label: string
  description?: string
  /** Choices used when the fleet's ceilings name none for this setting. */
  options?: string[]
  /** Choices the fleet still accepts for saved settings but no longer offers: shown only while the setting holds one. */
  savedOnlyOptions?: string[]
  /** A setting an older fleet service does not hold; its row shows only when the saved record carries it. */
  onlyWhenSaved?: boolean
  /** An `emails` list whose order means something: each entry shows its place and moves up or down. */
  ordered?: boolean
}

export interface FleetConfigActivityDefinition {
  activity: FleetConfigActivity
  title: string
  fields: FleetSettingField[]
}

export const FLEET_MODEL_OPTIONS = ['haiku', 'sonnet', 'opus']

// The repos the delivery lane may work in (the workspace's bin/lane-dispatch.sh). The plugin and
// workspace repos hold the lane's own rules, so they are never offered.
const FLEET_REPO_OPTIONS = [
  'adx',
  'merchant-dashboard',
  'tattle.net',
  'tattle-dagster',
  'tattle-playwright',
  'tattle-mobile',
  'tttl.co-customer-web-app',
  'tattle-inbox',
  'tattle-dbt',
  'tattle-site',
  'tattle-network'
]

const budgetField: FleetSettingField = {
  key: 'budgetUsd',
  kind: 'number',
  label: 'Budget per run (USD)',
  description: 'A run stops when its model cost reaches this.'
}

const enabledField: FleetSettingField = { key: 'enabled', kind: 'switch', label: 'Status' }

/** Where an activity's runs go; the fleet's ceilings narrow the choices an activity offers. */
const whereField: FleetSettingField = {
  key: 'where',
  kind: 'one-of',
  label: 'Runs on',
  description:
    "runners-first: offered to an engineer's runner, then AgentCore if none takes it. runners-only: waits for a runner, never AgentCore. agentcore: no runner is asked.",
  options: ['runners-first', 'runners-only', 'agentcore'],
  onlyWhenSaved: true
}

const RUNNERS_ONLY_WAITS = "runners-only waits for the author's own runner or another engineer's and never uses AgentCore."

/** The PR loop places lane PRs and every other PR separately; a fleet that has not split them yet sends one `where`. */
const prLoopWhereFields: FleetSettingField[] = [
  {
    ...whereField,
    key: 'whereLane',
    label: 'Runs on: lane PRs',
    description: `Reviews and fixes of the PRs unattended runs opened (the delivery lane and other fleet runs). ${RUNNERS_ONLY_WAITS}`
  },
  {
    ...whereField,
    key: 'whereOthers',
    label: 'Runs on: other PRs',
    description: `Reviews and fixes of every other PR: those of the engineers under Engineers' PRs, and every PR while Reviews is all. ${RUNNERS_ONLY_WAITS}`
  },
  whereField
]

// Anthropic's list prices for Haiku 5.5, Sonnet 5.5 and Opus 5.5: $0.10 / $2 / $4 per million input tokens.
const MODEL_COST_HINT = 'Haiku costs about a twentieth of Sonnet and Opus about twice; Haiku suits short, simple runs.'

/** The model an activity's runs use, on AgentCore and on laptop runners alike; a fleet that predates it holds none. */
const modelField: FleetSettingField = {
  key: 'model',
  kind: 'one-of',
  label: 'Model',
  description: `The model this loop's runs use. ${MODEL_COST_HINT}`,
  options: FLEET_MODEL_OPTIONS,
  onlyWhenSaved: true
}

/** The PR loop's reviews and fixes each have their own model: a fix writes code. */
const prLoopModelFields: FleetSettingField[] = [
  { ...modelField, key: 'reviewModel', label: 'Model: reviews', description: `The model each review uses. ${MODEL_COST_HINT}` },
  { ...modelField, key: 'fixModel', label: 'Model: fixes', description: `The model each fix uses; a fix writes code. ${MODEL_COST_HINT}` }
]

const capFields: FleetSettingField[] = [
  {
    key: 'dailyCapUsd',
    kind: 'number',
    label: 'Daily cap (USD)',
    description: 'Scheduled and queued runs stop starting once today\'s model cost reaches this. 0 means no cap. Starts from this page still run.',
    onlyWhenSaved: true
  },
  {
    key: 'monthlyCapUsd',
    kind: 'number',
    label: 'Monthly cap (USD)',
    description: 'The same for this month\'s model cost. 0 means no cap.',
    onlyWhenSaved: true
  }
]

/** Every configurable activity, in the order the Settings tab shows them. */
export const FLEET_CONFIG_ACTIVITIES: FleetConfigActivityDefinition[] = [
  {
    activity: 'backlog-triage',
    title: 'Backlog triage',
    fields: [
      enabledField,
      { key: 'runAt', kind: 'time', label: 'Runs at', description: 'New York time.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only' },
      { key: 'ticketsPerDay', kind: 'number', label: 'Tickets per day' },
      { key: 'autoPost', kind: 'switch', label: 'Post results automatically' },
      { ...whereField, description: 'Backlog triage runs on AgentCore only.' },
      modelField,
      budgetField,
      ...capFields
    ]
  },
  {
    activity: 'lpe-loop',
    title: 'Delivery loop (LPE)',
    fields: [
      enabledField,
      { key: 'tickTimes', kind: 'times', label: 'Tick times', description: 'New York time.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only' },
      {
        key: 'difficultyAgentcore',
        kind: 'multi',
        label: 'Difficulty on AgentCore',
        description: "The tickets the fleet's own AgentCore lane may take. The Delivery queue on the Overview shows them.",
        options: ['easy', 'medium']
      },
      {
        key: 'difficultyRunners',
        kind: 'multi',
        label: 'Difficulty on runners',
        description: "The tickets every engineer's runner lane may take. Easy and medium is what the old loop took.",
        options: ['easy', 'medium']
      },
      {
        key: 'repos',
        kind: 'multi',
        label: 'Repos',
        description: 'None selected means any allowed repo; otherwise only the ones selected.',
        options: FLEET_REPO_OPTIONS
      },
      { key: 'maxOpenPrs', kind: 'number', label: 'Most open PRs', description: '0 means no limit.' },
      { key: 'fixRoundsPerPr', kind: 'number', label: 'Fix rounds per PR' },
      {
        ...whereField,
        description:
          "The tick times above run the fleet's own lane on AgentCore. Runners do not take those ticks: once Delivery-lane ticks is in the runners' kinds, each runner ticks its own lane, with the build tools, at the runner cadence below."
      },
      modelField,
      {
        key: 'deliveriesPerDay',
        kind: 'number',
        label: 'AgentCore deliveries per day',
        description: "New tickets the fleet's own lane starts on AgentCore in a day. After that it only answers its open PRs. 0 stops new tickets there. Runners' deliveries are not counted here."
      },
      {
        key: 'laptopRetickAfterWorkMinutes',
        kind: 'number',
        label: 'Runner: next tick after work (minutes)',
        description: "How long after a runner's tick that did work its next tick starts. The engineers' own lane loop used 5.",
        onlyWhenSaved: true
      },
      {
        key: 'laptopTickEveryMinutes',
        kind: 'number',
        label: 'Runner: next tick when idle (minutes)',
        description: "How long after any other tick of a runner's lane (nothing to take, review only, waiting) the next one starts. The engineers' own lane loop used 20. The lane uses one of the runner's runs at once; between ticks other work may use it.",
        onlyWhenSaved: true
      },
      {
        key: 'laptopDeliveriesPerRunnerPerDay',
        kind: 'number',
        label: 'Deliveries per runner per day',
        description: "New tickets one runner's lane starts in a day. After that its ticks only answer the lane's open PRs. 0 means no limit, as when engineers ran the lane themselves.",
        onlyWhenSaved: true
      },
      {
        key: 'deliveryBudgetUsd',
        kind: 'number',
        label: 'Budget per delivery (USD)',
        description: 'A delivery stops when its model cost reaches this.'
      },
      {
        key: 'budgetUsd',
        kind: 'number',
        label: 'Budget per tick (USD)',
        description: 'Each scheduled check. A tick with nothing to do ends before it costs anything.'
      },
      ...capFields
    ]
  },
  {
    activity: 'qa-verify',
    title: 'QA verify',
    fields: [
      enabledField,
      {
        key: 'scope',
        kind: 'one-of',
        label: 'Scope',
        description:
          'What AgentCore verifies. Runners that take QA verify every ticket; a ticket outside this scope waits for a runner and never runs on AgentCore.',
        options: ['lane', 'limit', 'all']
      },
      { key: 'perDay', kind: 'number', label: 'Tickets per day', description: 'Used when the scope is limit.' },
      { key: 'envs', kind: 'multi', label: 'Environments', options: ['dev', 'staging'] },
      { ...whereField, description: `${whereField.description} This is for the tickets the scope gives AgentCore; every other ticket is runner-only.` },
      modelField,
      budgetField
    ]
  },
  {
    activity: 'pr-loop',
    title: 'PR loop',
    fields: [
      enabledField,
      {
        key: 'reviews',
        kind: 'one-of',
        label: 'Reviews',
        options: ['none', 'lane', 'all'],
        description:
          "lane: review each push to a PR the fleet opened. all: every open PR, offered to its author's runner first; runners then stop their own reviews."
      },
      {
        key: 'fixes',
        kind: 'one-of',
        label: 'Fixes',
        options: ['none', 'lane', 'opted-in'],
        savedOnlyOptions: ['opted-in'],
        description: "lane: fix review findings on PRs the fleet opened. An engineer's own PRs are fixed only when they are on Reviews + fixes under Engineers' PRs."
      },
      { key: 'roundsPerPrPerDay', kind: 'number', label: 'Rounds per PR per day', description: 'Fix runs and review runs each, per PR, per day.' },
      ...prLoopWhereFields,
      ...prLoopModelFields,
      budgetField,
      ...capFields
    ]
  },
  {
    activity: 'mention-sweep',
    title: 'Mention sweep',
    fields: [
      enabledField,
      { key: 'slots', kind: 'number', label: 'Slots' },
      { key: 'jiraWrites', kind: 'switch', label: 'Write to Jira' },
      whereField,
      modelField,
      budgetField,
      ...capFields
    ]
  },
  {
    activity: 'talk',
    title: 'Talk it over',
    fields: [
      {
        ...enabledField,
        description: 'Off hides Talk it over and Ask Claude in ADX. Conversations already held are kept until they expire.'
      },
      {
        ...modelField,
        description: `The model Claude answers with. It runs inside the fleet service, not on a runner. ${MODEL_COST_HINT}`
      },
      {
        key: 'turnsPerDay',
        kind: 'number',
        label: 'Questions a day',
        description: 'How many questions each person may ask in a New York day, across every conversation. Blank for no limit. 0 lets people read conversations but ask nothing.'
      },
      {
        key: 'toolCalls',
        kind: 'number',
        label: 'Reads per answer',
        description: 'The most tickets, pull requests, files and pages Claude may read for one answer before it answers with what it has.'
      },
      {
        key: 'prodReads',
        kind: 'switch',
        label: 'Production reads',
        description:
          "On (the default): Claude may read production, read only, as an engineer's session does: Datadog, the read-only databases with personal data masked, the message logs, and AWS alarms, metrics, logs, queue counts, topic subscriptions and Lambda settings. It never receives, purges or redrives a message. Off: Jira, Bitbucket, Confluence and the fleet's records only.",
        onlyWhenSaved: true
      },
      {
        key: 'actions',
        kind: 'switch',
        label: 'Act from the chat',
        description:
          "On: a Claude page's actions can be proposed in Ask Claude as a Confirm button; confirming calls the same request the page's button does. The chat itself never changes anything. Off by default.",
        onlyWhenSaved: true
      }
    ]
  },
  {
    activity: 'laptop-runners',
    title: 'Runners',
    fields: [
      {
        ...enabledField,
        description: 'Off pauses every runner: work runs on AgentCore, and QA outside its scope waits until they are back on.'
      },
      {
        key: 'kinds',
        kind: 'multi',
        label: 'Work runners may take',
        description:
          'Every runner takes these unless its engineer opted out of one, and only what it can do. Three are left out until added here: delivery-lane ticks (lpe-loop) write code on the runner\'s machine, QA fields audits (qa-fields-audit) write Jira fields as the runner\'s engineer, and queue sizing (lane-size) edits Jira labels, so add them only when ready.',
        options: ['pr-review', 'pr-fix', 'mention-sweep', 'qa-verify', 'release-verify', 'lpe-loop', 'fixme-sweep', 'qa-fields-audit', 'lane-size', 'cover']
      },
      {
        key: 'maxItemsPerDay',
        kind: 'number',
        label: 'Most items per runner per day',
        description: "A ceiling on each runner's own daily cap. 0 means no ceiling."
      },
      {
        key: 'maxSlots',
        kind: 'number',
        label: 'Most runs at once per runner',
        description: "A ceiling on each runner's own setting."
      },
      {
        key: 'defaultDayShare7d',
        kind: 'number',
        label: "Daily share of each owner's week (%)",
        description:
          "The most of an owner's 7-day Claude window fleet work uses in one New York day, until the owner picks their own in /runner setup. 0 is no limit. A default only: an owner's choice always wins, and runners that were already working before this default existed keep no daily share until their owner picks. Running jobs finish, and the owner's own PRs are not counted.",
        onlyWhenSaved: true
      },
      {
        key: 'defaultReserve7d',
        kind: 'number',
        label: 'Kept for the owner (% of the week)',
        description:
          "The share of an owner's 7-day Claude window a runner never uses, until the owner picks their own. A default only, for runners new since this default existed; older runners keep their saved reserve until their owner picks.",
        onlyWhenSaved: true
      },
      {
        key: 'runnerMinPluginVersion',
        kind: 'text',
        label: 'Restart /runner windows older than',
        description:
          'Empty by default: a /runner window older than the plugins on its machine keeps working and only asks to be restarted. For an urgent fix, enter a tattle-claude-plugins commit: a window that loaded none of it or later takes no new work until it is restarted.',
        onlyWhenSaved: true
      }
    ]
  },
  {
    activity: 'release-watch',
    title: 'Release watcher',
    fields: [
      {
        ...enabledField,
        description: 'Off by default. Watches the due fix version and verifies the release in prod once every repo in it is live.'
      },
      {
        key: 'autoRelease',
        kind: 'switch',
        label: 'Mark the release done in Jira',
        description: 'Off: when every repo is live it only DMs the owner "all green, click Release", and starts nothing.'
      },
      {
        key: 'stuckHours',
        kind: 'number',
        label: 'Hours before a lagging repo is flagged',
        description: 'After the first repo goes live, the owner hears once about any repo still not live.'
      },
      {
        key: 'runOn',
        kind: 'one-of',
        label: 'Run the prod verification on',
        description:
          "Runners: runners only (they have the prod test logins). Runners first / AgentCore need the runner role granted the prod test logins, which isn't done yet, and cost is being measured first.",
        options: ['runners', 'runners-first', 'agentcore']
      },
      modelField,
      { ...budgetField, label: 'Budget for the prod verification (USD)' },
      {
        key: 'nightlyTarget',
        kind: 'one-of',
        label: 'Point the Playwright nightly',
        description:
          'On: when a release is cut the nightly moves to its release branch on staging, and back to develop on dev once it is in prod; the watcher updates the schedule, opens a PR for the VS Code default and posts one line in #dev-qa. Log only: it only logs what it would change. Off: switched by hand.',
        options: ['off', 'log-only', 'on'],
        onlyWhenSaved: true
      }
    ]
  },
  {
    activity: 'release-staging-qa',
    title: 'Release staging QA',
    fields: [
      {
        ...enabledField,
        description:
          'Off by default. Once every repo in the newest cut release is on staging, each of its tickets is offered to the open runners for a staging QA pass.'
      },
      {
        key: 'partialStart',
        kind: 'switch',
        label: 'Start repo by repo',
        description:
          'On: a ticket is offered as soon as every repo it touches is on staging. Off: nothing starts until every repo in the release is.'
      },
      {
        key: 'maxQueued',
        kind: 'number',
        label: 'Most tickets offered at once',
        description:
          'A safety ceiling across the whole fleet, not a pace: each runner still takes tickets only within its own slots, so more runners finish sooner.'
      },
      modelField
    ]
  },
  {
    activity: 'fixme-sweep',
    title: 'Playwright suite sweep',
    fields: [
      {
        ...enabledField,
        description:
          "Off by default. Each night the fleet splits last night's failing Playwright tests and the quarantined ones into one job per test file and shares them out to runners, then opens one PR that quarantines the new failures and restores the ones that pass again."
      },
      { key: 'runAt', kind: 'time', label: 'Runs at', description: 'New York time. After the nightly has finished, which is around 11 PM.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only' },
      {
        key: 'heal',
        kind: 'switch',
        label: 'Fix tests that need updating',
        description: 'On: a test that fails because the app changed on purpose is fixed and opened as a draft PR for review, one per test file.'
      },
      {
        key: 'maxHeals',
        kind: 'number',
        label: 'Most test fixes per night',
        description: 'The rest stay quarantined and are tried on a later night.'
      },
      {
        key: 'preferRunners',
        kind: 'emails',
        ordered: true,
        label: 'Runs first on',
        description: "Whose runners are offered the night's jobs first, in this order. Empty: any runner, from the start.",
        onlyWhenSaved: true
      },
      {
        key: 'fallbackMinutes',
        kind: 'number',
        label: 'Wait for them (minutes)',
        description: 'Then any runner whose owner opted in to suite sweeps may take a job, the one with the most Claude usage left first. 0 offers it to them at once.',
        onlyWhenSaved: true
      },
      {
        key: 'itemUsagePct',
        kind: 'number',
        label: "A job's typical Claude use (% of a week)",
        description: "A job is never offered to a runner whose daily share or reserve leaves less than this today.",
        onlyWhenSaved: true
      },
      { ...whereField, description: 'Runs on runners only, so it adds nothing to the AWS bill.', options: ['runners-only'] },
      modelField
    ]
  },
  {
    activity: 'qa-fields-audit',
    title: 'QA fields audit',
    fields: [
      {
        ...enabledField,
        description:
          'Off by default. At each run time an open runner fills missing QA Assignee and QA Failure Reasons and corrects inflated QA Failure Counts on Jira tickets, so the QA dashboard\'s numbers are right. Every value it writes is traced to a comment or a status change on the ticket. Runners take it only once QA fields audits is ticked in the Runners card\'s Work runners may take.'
      },
      { key: 'runTimes', kind: 'times', label: 'Runs at', description: 'New York time. Up to four a day.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only' },
      { ...whereField, description: 'Runs on runners only, so it adds nothing to the AWS bill.', options: ['runners-only'] },
      modelField
    ]
  },
  {
    activity: 'lane-size',
    title: 'Size the delivery queue',
    fields: [
      {
        ...enabledField,
        description:
          'Off by default. A runner labels tickets the delivery lane could take as easy, medium or hard, and how much each matters, before a lane reaches them, and can fill in their severity, reach and location count in the same change. No comments. Runners take it only once Queue sizing (lane-size) is added to the work runners may take.'
      },
      { key: 'everyHours', kind: 'number', label: 'Every (hours)', description: 'How often a run is offered, between 07:00 and 19:00 New York time.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only', description: 'So no one gets these emails at the weekend.' },
      {
        key: 'newTickets',
        kind: 'switch',
        label: 'Size new tickets',
        description: 'On: a ticket filed in the last two days is sized at the next run, outside the daily number below.'
      },
      {
        key: 'backfillPerDay',
        kind: 'number',
        label: 'Old tickets a day',
        description:
          "Every label change emails the ticket's watchers, and Claude's Jira account cannot turn that off, so older tickets are sized a few a day across all runners, the ones the lane reaches first. 0 sizes only new tickets."
      },
      { key: 'maxPerRun', kind: 'number', label: 'Most per run', description: 'A ceiling on one run, new tickets included.' },
      {
        key: 'fillFields',
        kind: 'switch',
        label: 'Fill Severity, Reach and Location Count',
        description:
          "On: in the same change as the labels, a field still at Jira's default (Graphical, A single user, 1-10) is set from what the ticket says, with the reason kept in the run's record. A value someone chose is never changed. Off: labels only."
      },
      { ...whereField, description: 'Runs on runners only, so it adds nothing to the AWS bill.', options: ['runners-only'] },
      modelField
    ]
  },
  {
    activity: 'cover',
    title: 'Fill test coverage gaps',
    fields: [
      {
        ...enabledField,
        description:
          'Off by default. A runner takes the most severe open coverage gaps, writes the missing unit and Playwright tests without changing application code, and opens draft pull requests for the ones that pass a test-only check. A saved list of work runners may take needs Coverage gaps (cover) ticked as well.'
      },
      { key: 'everyHours', kind: 'number', label: 'Every (hours)', description: 'How often a run is offered to runners, from 00:40 New York time around the clock.' },
      { key: 'weekdaysOnly', kind: 'switch', label: 'Weekdays only', description: 'So draft pull requests arrive when reviewers are in.' },
      {
        key: 'findingsPerRun',
        kind: 'number',
        label: 'Gaps per run',
        description: 'How many coverage gaps one run takes, most severe first. Each can mean a draft pull request to review.'
      },
      { ...whereField, description: 'Runs on runners only: it builds and runs tests in the engineer\'s own clones.', options: ['runners-only'] },
      modelField
    ]
  },
  {
    activity: 'pipelines',
    title: 'Pipeline health',
    fields: [
      {
        ...enabledField,
        description: "Tracks main, develop and release builds from Bitbucket's build webhooks and a slow backstop poll, for the Pipelines page."
      },
      {
        key: 'autoFix',
        kind: 'switch',
        label: 'Diagnose and fix main and develop on their own',
        description:
          "When main or develop turns red, Claude diagnoses it once per breaking commit and opens a fix PR for normal review. Never on release branches, never merges. A flaky or machine failure is re-run once instead. Runs follow the PR loop's caps and fix model."
      },
      { key: 'alerts', kind: 'switch', label: 'Post alerts to #dev-qa', description: 'One line per incident, edited in place when it clears. Mentions nobody.' },
      { key: 'redAfterMinutes', kind: 'number', label: 'Alert when a branch is red for (minutes)' },
      { key: 'stuckAfterMinutes', kind: 'number', label: 'Alert when a run is stuck for (minutes)' },
      { key: 'scheduleFailsInARow', kind: 'number', label: 'Alert when a schedule fails this many times in a row' },
      { key: 'pollMinutes', kind: 'number', label: 'Backstop poll every (minutes)', description: 'Webhooks keep the tab current; the poll catches what they miss.' },
      { key: 'fixesPerBranchPerDay', kind: 'number', label: 'Automatic fixes per branch per day', description: '0 stops automatic fixes; Diagnose and Fix it on the tab still run.' },
      {
        key: 'halted',
        kind: 'one-of',
        label: 'Check halted deploys',
        description:
          "Bitbucket holds a deploy back while another build deploys to the same environment. On: the Pipelines page sorts the held builds, folding away the ones a newer build replaced. Off: held builds are not checked. Bitbucket's API can neither stop nor resume a held build, so a person does that in Bitbucket.",
        options: ['off', 'on'],
        onlyWhenSaved: true
      }
    ]
  },
  {
    activity: 'fleet',
    title: 'Fleet',
    fields: [
      { key: 'monthlyBudgetUsd', kind: 'number', label: 'Monthly budget (USD)' },
      { key: 'atBudget', kind: 'one-of', label: 'At the budget', options: ['alert', 'pause'] },
      {
        key: 'operators',
        kind: 'emails',
        label: 'Operators',
        description: 'Who can change these settings. @gettattle.com addresses only, at least one.'
      },
      { key: 'models', kind: 'models', label: 'Model per agent', description: 'Agents not listed use their own default.' },
      {
        key: 'browser',
        kind: 'one-of',
        label: 'Browser',
        description:
          "Where runs' browsers run. agentcore is AWS's managed browser, which can be watched live and taken over; local is the runner's own. The next run uses it.",
        options: ['local', 'agentcore']
      },
      {
        key: 'myWorkActionsPerDay',
        kind: 'number',
        label: 'My work actions per person per day',
        description: 'Actions each engineer can start from the My work page per day. 0 turns them off. Operators have no limit.'
      },
      {
        key: 'myWorkActionsPerPerson',
        kind: 'email-numbers',
        label: 'My work actions per day, per person',
        description:
          'A different number for one person, by @gettattle.com email. Anyone not listed gets the number above. Operators have no limit, whatever this says.',
        onlyWhenSaved: true
      }
    ]
  }
]

/** The rows a settings card shows: every field, less ones the fleet's record does not hold yet. */
export function visibleSettingFields(fields: FleetSettingField[], settings: FleetSettings): FleetSettingField[] {
  return fields.filter(field => !field.onlyWhenSaved || field.key in settings)
}

const PR_LOOP_SCOPES = [
  { key: 'reviews', label: 'Reviews' },
  { key: 'fixes', label: 'Fixes' }
] as const

/** What an empty Engineers' PRs list leaves: only the PRs the PR loop's Reviews and Fixes cover get Claude. */
export function engineersPrsEmptyText(settings: FleetSettings): string {
  const covering = PR_LOOP_SCOPES.filter(scope => settings[scope.key] !== 'none')
  if (!covering.length) return 'No one yet — no PRs get Claude automatically.'
  return `No one yet — only the PRs ${coveringScopesText(covering, settings)} get Claude.`
}

/** "Reviews and Fixes cover (lane)", "Reviews (all) and Fixes (lane) cover" or "Fixes covers (lane)". */
function coveringScopesText(covering: readonly { key: string; label: string }[], settings: FleetSettings): string {
  const values = covering.map(scope => settings[scope.key])
  const named = (value: FleetSettingValue | undefined) => (typeof value === 'string' ? ` (${value})` : '')
  if (covering.length === 1) return `${covering[0]!.label} covers${named(values[0])}`
  if (values[0] === values[1]) return `Reviews and Fixes cover${named(values[0])}`
  return `Reviews${named(values[0])} and Fixes${named(values[1])} cover`
}

/** The choices a setting offers: the fleet's, else the ones this page knows, less any kept only for a saved value
 *  the setting does not hold. */
export function settingOptions(
  field: FleetSettingField,
  ceilings: FleetActivityCeilings | undefined,
  current?: FleetSettingValue
): string[] {
  const all = ceilings?.[field.key]?.options ?? field.options ?? []
  const savedOnly = field.savedOnlyOptions ?? []
  return all.filter(option => !savedOnly.includes(option) || option === current)
}

/** Wide ranges such as a monthly budget step by 50, so reaching a value does not take thousands of clicks. */
export function numberStep(ceiling: FleetSettingCeiling | undefined): number {
  if (ceiling?.min === undefined || ceiling?.max === undefined) return 1
  return ceiling.max - ceiling.min >= 500 ? 50 : 1
}

/**
 * A deep copy, so editing a card's draft never reaches the saved record it started from. Settings are
 * plain JSON, and a JSON round trip also copies a reactive record, which `structuredClone` refuses.
 */
export function cloneSettings(settings: FleetSettings): FleetSettings {
  return JSON.parse(JSON.stringify(settings))
}

/** `ordered`: the setting is an `ordered` list, so the same entries in another order are a change. */
export interface SettingCompareOptions {
  ordered?: boolean
}

export function isSettingEqual(
  left: FleetSettingValue | undefined,
  right: FleetSettingValue | undefined,
  options: SettingCompareOptions = {}
): boolean {
  return JSON.stringify(normalize(left, options)) === JSON.stringify(normalize(right, options))
}

/** The partial body for `POST /fleet/config/<activity>`: only the settings the draft changed. `fields` names the
 *  `ordered` lists, where a reorder alone is a change. */
export function changedSettings(saved: FleetSettings, draft: FleetSettings, fields: FleetSettingField[] = []): FleetSettings {
  const orderedKeys = new Set(fields.filter(field => field.ordered).map(field => field.key))
  return Object.fromEntries(
    Object.entries(draft).filter(([key, value]) => !isSettingEqual(saved[key], value, { ordered: orderedKeys.has(key) }))
  )
}

/** The setting an API validation error names, so the message can sit under that input. */
export function fieldNamedInError(message: string | null, fields: FleetSettingField[]): string | null {
  if (!message) return null
  const named = fields.find(field => new RegExp(`\\b${field.key}\\b`).test(message))
  return named?.key ?? null
}

// A list's order carries meaning only for an `ordered` list, and a model map's key order never does, so
// otherwise the same entries compare equal.
function normalize(value: FleetSettingValue | undefined, options: SettingCompareOptions): unknown {
  if (value === undefined || value === null) return null
  if (Array.isArray(value)) return options.ordered ? [...value] : [...value].sort()
  if (typeof value !== 'object') return value
  return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
}
